import * as THREE from 'three'
import { OrbitControls } from 'three/addons/controls/OrbitControls.js'
import { LineSegments2 } from 'three/addons/lines/LineSegments2.js'
import { LineSegmentsGeometry } from 'three/addons/lines/LineSegmentsGeometry.js'
import { LineMaterial } from 'three/addons/lines/LineMaterial.js'
;(function () {
  const clamp = (v, min, max) => Math.min(Math.max(v, min), max)

  // Three.js state (module-level, one renderer per page)
  let _renderer = null
  let _scene = null
  let _camera = null
  let _controls = null
  let _boxMesh = null
  let _lineMesh = null
  let _cameraReady = false
  let _autoRotateRAF = null
  let _baseMaxDim = 0
  let _baseDist = 0
  let _lastMaxDim = 0

  const COLOR_MAP = {
    brak: 0xa6a06a,
    teak: 0x8b5a2b,
    orzech: 0x5c3317,
    szary: 0x808080,
  }

  // Real planter construction constants (cm): bottom board nailed on from
  // below, inset 1 cm around the perimeter, with 2×2 cm runner legs along
  // the longer side - together the "+ 4 cm dno oraz nóżki".
  const BOTTOM_TH_CM = 2
  const BOTTOM_INSET_CM = 1
  const LEG_SIZE_CM = 2
  const LEG_HEIGHT_CM = 2

  // Procedural non-woven agrotextile texture: dark grey base (not pure
  // black) with speckle noise and short random fibers
  let _fabricTex = null
  const getFabricTexture = () => {
    if (_fabricTex) return _fabricTex

    const size = 256
    const c = document.createElement('canvas')
    c.width = c.height = size
    const ctx = c.getContext('2d')

    ctx.fillStyle = '#000000'
    ctx.fillRect(0, 0, size, size)

    for (let i = 0; i < 10000; i++) {
      const g = 8 + Math.random() * 26
      ctx.globalAlpha = 0.12 + Math.random() * 0.22
      ctx.fillStyle = `rgb(${g},${g},${g})`
      ctx.fillRect(Math.random() * size, Math.random() * size, 1.5, 1.5)
    }

    ctx.globalAlpha = 1
    ctx.lineWidth = 0.8
    for (let i = 0; i < 600; i++) {
      const x = Math.random() * size
      const y = Math.random() * size
      const a = Math.random() * Math.PI
      const l = 5 + Math.random() * 14
      const g = 40 + Math.random() * 35
      ctx.strokeStyle = `rgba(${g},${g},${g},0.13)`
      ctx.beginPath()
      ctx.moveTo(x, y)
      ctx.lineTo(x + Math.cos(a) * l, y + Math.sin(a) * l)
      ctx.stroke()
    }

    _fabricTex = new THREE.CanvasTexture(c)
    _fabricTex.wrapS = _fabricTex.wrapT = THREE.RepeatWrapping
    _fabricTex.repeat.set(2, 2)
    return _fabricTex
  }

  // Procedural wood grain: near-white base with darker wavy streaks and
  // knots, multiplied by each plank's material color at render time
  let _woodTex = null
  const getWoodTexture = () => {
    if (_woodTex) return _woodTex

    const w = 512
    const h = 256
    const c = document.createElement('canvas')
    c.width = w
    c.height = h
    const ctx = c.getContext('2d')

    ctx.fillStyle = '#ffffff'
    ctx.fillRect(0, 0, w, h)

    // long wavy grain streaks running along the plank
    for (let i = 0; i < 130; i++) {
      const y0 = Math.random() * h
      const amp = 2 + Math.random() * 6
      const period = 60 + Math.random() * 160
      const g = 115 + Math.random() * 85
      ctx.strokeStyle = `rgba(${g},${g},${g},${0.18 + Math.random() * 0.22})`
      ctx.lineWidth = 0.5 + Math.random() * 2.2
      ctx.beginPath()
      for (let x = 0; x <= w; x += 8) {
        const y = y0 + Math.sin((x / period) * Math.PI * 2 + i) * amp
        if (x === 0) ctx.moveTo(x, y)
        else ctx.lineTo(x, y)
      }
      ctx.stroke()
    }

    // a few knots with faint rings
    for (let i = 0; i < 5; i++) {
      const kx = Math.random() * w
      const ky = Math.random() * h
      for (let r = 6; r >= 1; r -= 1.5) {
        const g = 120 + r * 12
        ctx.strokeStyle = `rgba(${g},${g},${g},0.25)`
        ctx.lineWidth = 1
        ctx.beginPath()
        ctx.ellipse(kx, ky, r * 2.2, r, 0, 0, Math.PI * 2)
        ctx.stroke()
      }
    }

    // fine surface noise
    for (let i = 0; i < 6000; i++) {
      const g = 150 + Math.random() * 100
      ctx.globalAlpha = 0.04 + Math.random() * 0.08
      ctx.fillStyle = `rgb(${g},${g},${g})`
      ctx.fillRect(Math.random() * w, Math.random() * h, 1.5, 1)
    }
    ctx.globalAlpha = 1

    _woodTex = new THREE.CanvasTexture(c)
    _woodTex.wrapS = _woodTex.wrapT = THREE.RepeatWrapping
    return _woodTex
  }

  // Tears down the current WebGL context/scene so initScene() can build a
  // fresh one from scratch - used to recover from a failed first render
  // (see safeCalculate below) without requiring a full page reload.
  const resetThreeState = () => {
    if (_autoRotateRAF) {
      cancelAnimationFrame(_autoRotateRAF)
      _autoRotateRAF = null
    }
    if (_renderer) {
      _renderer.dispose()
    }
    _renderer = null
    _scene = null
    _camera = null
    _controls = null
    _boxMesh = null
    _lineMesh = null
    _cameraReady = false
    _baseMaxDim = 0
    _baseDist = 0
    _lastMaxDim = 0
  }

  const initScene = (canvas) => {
    if (_renderer) return

    _renderer = new THREE.WebGLRenderer({
      canvas,
      antialias: true,
      alpha: true,
    })
    _renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2))
    _renderer.setClearColor(0x000000, 0)

    _scene = new THREE.Scene()

    const w = canvas.offsetWidth || 495
    const h = Math.round(w * (355 / 495))
    _renderer.setSize(w, h)

    _camera = new THREE.PerspectiveCamera(28, w / h, 0.1, 5000)

    _controls = new OrbitControls(_camera, canvas)
    _controls.enablePan = false
    _controls.enableDamping = false
    _controls.autoRotate = false
    _controls.autoRotateSpeed = 0.6
    _controls.addEventListener('change', () =>
      _renderer.render(_scene, _camera),
    )
    // Manually rotating/zooming means the user has taken control - stop
    // auto-rotating, keep the toggle button in sync, and reveal "Resetuj
    // widok" (hidden until the user actually moves the view themselves).
    _controls.addEventListener('start', () => {
      const resetViewBtn = document.querySelector('#disp-reset-view')
      if (resetViewBtn) resetViewBtn.classList.remove('is-hidden')

      if (!_controls.autoRotate) return
      _controls.autoRotate = false
      const btn = document.querySelector('#disp-autorotate-toggle')
      if (btn) {
        btn.setAttribute('aria-pressed', 'false')
        btn.classList.remove('is-active')
      }
    })

    _scene.add(new THREE.AmbientLight(0xffffff, 0.6))

    // Key light aligned with the initial camera direction, so the faces
    // seen on load are the lit ones (as if the light came from the viewer).
    // Kept low (small y) so the specular highlight lands on the vertical
    // walls instead of only the top edges.
    const sun = new THREE.DirectionalLight(0xffffff, 3.0)
    sun.position.set(3.2, 1.2, 4.6)
    _scene.add(sun)

    // Soft top light so the rim and top faces stay defined
    const top = new THREE.DirectionalLight(0xffffff, 0.7)
    top.position.set(0, 6, 2)
    _scene.add(top)

    // Two fill lights covering the back so it stays readable when rotating
    const fill = new THREE.DirectionalLight(0xffffff, 1.1)
    fill.position.set(-3, 1, -4)
    _scene.add(fill)

    const fill2 = new THREE.DirectionalLight(0xffffff, 0.7)
    fill2.position.set(4, 2, -3)
    _scene.add(fill2)
  }

  const buildLineGeo = (W, H, D, seg, t, under) => {
    const pts = []

    const addBoxEdges = (cx, cy, cz, sx, sy, szz) => {
      const X1 = cx - sx / 2,
        X2 = cx + sx / 2
      const Y1 = cy - sy / 2,
        Y2 = cy + sy / 2
      const Z1 = cz - szz / 2,
        Z2 = cz + szz / 2
      const edges = [
        [X1, Y1, Z1, X2, Y1, Z1],
        [X2, Y1, Z1, X2, Y1, Z2],
        [X2, Y1, Z2, X1, Y1, Z2],
        [X1, Y1, Z2, X1, Y1, Z1],
        [X1, Y2, Z1, X2, Y2, Z1],
        [X2, Y2, Z1, X2, Y2, Z2],
        [X2, Y2, Z2, X1, Y2, Z2],
        [X1, Y2, Z2, X1, Y2, Z1],
        [X1, Y1, Z1, X1, Y2, Z1],
        [X2, Y1, Z1, X2, Y2, Z1],
        [X2, Y1, Z2, X2, Y2, Z2],
        [X1, Y1, Z2, X1, Y2, Z2],
      ]
      for (const e of edges) pts.push(...e)
    }
    const x1 = -W / 2,
      x2 = W / 2
    const y1 = -H / 2,
      y2 = H / 2
    const z1 = -D / 2,
      z2 = D / 2

    // 12 outer box edges
    const outerEdges = [
      [x1, y1, z1, x2, y1, z1],
      [x2, y1, z1, x2, y1, z2],
      [x2, y1, z2, x1, y1, z2],
      [x1, y1, z2, x1, y1, z1],
      [x1, y2, z1, x2, y2, z1],
      [x2, y2, z1, x2, y2, z2],
      [x2, y2, z2, x1, y2, z2],
      [x1, y2, z2, x1, y2, z1],
      [x1, y1, z1, x1, y2, z1],
      [x2, y1, z1, x2, y2, z1],
      [x2, y1, z2, x2, y2, z2],
      [x1, y1, z2, x1, y2, z2],
    ]
    for (const e of outerEdges) pts.push(...e)

    // Inner top rim (open top of hollow planter)
    const ix1 = x1 + t,
      ix2 = x2 - t
    const iz1 = z1 + t,
      iz2 = z2 - t
    pts.push(ix1, y2, iz1, ix2, y2, iz1)
    pts.push(ix2, y2, iz1, ix2, y2, iz2)
    pts.push(ix2, y2, iz2, ix1, y2, iz2)
    pts.push(ix1, y2, iz2, ix1, y2, iz1)

    // Horizontal segment lines around all 4 outer faces
    for (let i = 1; i < seg; i++) {
      const y = y1 + (H / seg) * i
      pts.push(x1, y, z2, x2, y, z2) // front
      pts.push(x2, y, z2, x2, y, z1) // right
      pts.push(x2, y, z1, x1, y, z1) // back
      pts.push(x1, y, z1, x1, y, z2) // left
    }

    // (no segment lines on the inner walls - the fabric lining covers them)

    // Bottom board and runner legs below the walls
    if (under) {
      addBoxEdges(0, -H / 2 - under.b / 2, 0, under.bw, under.b, under.bd)
      const legY = -H / 2 - under.b - under.legH / 2
      if (under.alongZ) {
        for (const s of [-1, 1]) {
          addBoxEdges(
            s * (under.bw / 2 - under.legS / 2),
            legY,
            0,
            under.legS,
            under.legH,
            under.bd,
          )
        }
      } else {
        for (const s of [-1, 1]) {
          addBoxEdges(
            0,
            legY,
            s * (under.bd / 2 - under.legS / 2),
            under.bw,
            under.legH,
            under.legS,
          )
        }
      }
    }

    // Outlines around the exposed end-grain caps at alternating corners
    const segH = H / seg
    const addRectX = (x, yA, yB, zA, zB) => {
      pts.push(x, yA, zA, x, yB, zA)
      pts.push(x, yB, zA, x, yB, zB)
      pts.push(x, yB, zB, x, yA, zB)
      pts.push(x, yA, zB, x, yA, zA)
    }
    const addRectZ = (z, yA, yB, xA, xB) => {
      pts.push(xA, yA, z, xB, yA, z)
      pts.push(xB, yA, z, xB, yB, z)
      pts.push(xB, yB, z, xA, yB, z)
      pts.push(xA, yB, z, xA, yA, z)
    }
    for (let i = 0; i < seg; i++) {
      const yc = y1 + segH * (i + 0.5)
      const yA = yc - segH / 2
      const yB = yc + segH / 2

      if (i % 2 === 0) {
        // ends of the front/back planks, exposed at the left/right faces
        addRectX(x1, yA, yB, z2 - t, z2)
        addRectX(x2, yA, yB, z2 - t, z2)
        addRectX(x1, yA, yB, z1, z1 + t)
        addRectX(x2, yA, yB, z1, z1 + t)
      } else {
        // ends of the left/right planks, exposed at the front/back faces
        addRectZ(z1, yA, yB, x1, x1 + t)
        addRectZ(z2, yA, yB, x1, x1 + t)
        addRectZ(z1, yA, yB, x2 - t, x2)
        addRectZ(z2, yA, yB, x2 - t, x2)
      }
    }

    const geo = new LineSegmentsGeometry()
    geo.setPositions(pts)
    return geo
  }

  const drawBox = (canvas, sz, heightSeg, dl, seg, colorKey, thickness) => {
    if (!canvas) return

    initScene(canvas)

    if (_boxMesh) {
      _scene.remove(_boxMesh)
      _boxMesh.traverse((child) => {
        if (child.isMesh) {
          child.geometry.dispose()
          if (Array.isArray(child.material)) {
            child.material.forEach((m) => m.dispose())
          } else {
            child.material.dispose()
          }
        }
      })
    }
    if (_lineMesh) {
      _scene.remove(_lineMesh)
      _lineMesh.geometry.dispose()
      _lineMesh.material.dispose()
    }

    const S = 0.01 // cm → scene units
    const W = sz * S
    const H = heightSeg * S
    const D = dl * S
    const t = thickness * S

    const baseColor = new THREE.Color(COLOR_MAP[colorKey] ?? COLOR_MAP.brak)
    const altColor = baseColor.clone().multiplyScalar(0.88)

    const woodTex = getWoodTexture()

    const mat = new THREE.MeshStandardMaterial({
      color: baseColor,
      map: woodTex,
      bumpMap: woodTex,
      bumpScale: 0.024,
      roughness: 0.4,
      metalness: 0.0,
      side: THREE.DoubleSide,
    })

    // Slightly darker variant used on every other plank for subtle shading
    const matAlt = new THREE.MeshStandardMaterial({
      color: altColor,
      map: woodTex,
      bumpMap: woodTex,
      bumpScale: 0.008,
      roughness: 0.4,
      metalness: 0.0,
      side: THREE.DoubleSide,
    })

    // Even darker variants for the exposed end-grain caps at the corners
    const matEnd = new THREE.MeshStandardMaterial({
      color: baseColor.clone().multiplyScalar(0.7),
      map: woodTex,
      bumpMap: woodTex,
      bumpScale: 0.008,
      roughness: 0.55,
      metalness: 0.0,
      side: THREE.DoubleSide,
    })
    const matAltEnd = new THREE.MeshStandardMaterial({
      color: altColor.clone().multiplyScalar(0.7),
      map: woodTex,
      bumpMap: woodTex,
      bumpScale: 0.008,
      roughness: 0.55,
      metalness: 0.0,
      side: THREE.DoubleSide,
    })

    const group = new THREE.Group()

    // BoxGeometry material order: [+x, -x, +y, -y, +z, -z]
    const addPlank = (width, height, depth, x, y, z, material, endMaterial, endAxis) => {
      let mats = material
      if (endMaterial && endAxis === 'x') {
        mats = [endMaterial, endMaterial, material, material, material, material]
      } else if (endMaterial && endAxis === 'z') {
        mats = [material, material, material, material, endMaterial, endMaterial]
      }
      const plank = new THREE.Mesh(new THREE.BoxGeometry(width, height, depth), mats)
      plank.position.set(x, y, z)
      group.add(plank)
    }

    // Stack the four walls level by level, alternating which pair of planks
    // runs full-length and overlaps the corner (like real log-style joints)
    // - the other pair fits in between. The exposed ends of the long planks
    // get a darker "end grain" shade with an outline (added in buildLineGeo).
    const segH = H / seg
    for (let i = 0; i < seg; i++) {
      const y = -H / 2 + segH * (i + 0.5)

      if (i % 2 === 0) {
        // front/back planks run full width, left/right fit between them
        addPlank(W, segH, t, 0, y, D / 2 - t / 2, mat, matEnd, 'x')
        addPlank(W, segH, t, 0, y, -D / 2 + t / 2, mat, matEnd, 'x')
        addPlank(t, segH, D - 2 * t, -W / 2 + t / 2, y, 0, mat)
        addPlank(t, segH, D - 2 * t, W / 2 - t / 2, y, 0, mat)
      } else {
        // left/right planks run full depth, front/back fit between them
        addPlank(t, segH, D, -W / 2 + t / 2, y, 0, matAlt, matAltEnd, 'z')
        addPlank(t, segH, D, W / 2 - t / 2, y, 0, matAlt, matAltEnd, 'z')
        addPlank(W - 2 * t, segH, t, 0, y, D / 2 - t / 2, matAlt)
        addPlank(W - 2 * t, segH, t, 0, y, -D / 2 + t / 2, matAlt)
      }
    }

    // Bottom board nailed on from below, inset 1 cm around the perimeter
    const b = BOTTOM_TH_CM * S
    const inset = BOTTOM_INSET_CM * S
    const legS = LEG_SIZE_CM * S
    const legH = LEG_HEIGHT_CM * S

    const bw = Math.max(W - 2 * inset, legS)
    const bd = Math.max(D - 2 * inset, legS)

    const bottomMesh = new THREE.Mesh(new THREE.BoxGeometry(bw, b, bd), mat)
    bottomMesh.position.y = -H / 2 - b / 2
    group.add(bottomMesh)

    // Runner legs (2×2 cm cross-section) along the longer side, flush with
    // the edges of the bottom board, in a darker shade than the planter
    const legMat = new THREE.MeshStandardMaterial({
      color: baseColor.clone().multiplyScalar(0.55),
      map: woodTex,
      bumpMap: woodTex,
      bumpScale: 0.008,
      roughness: 0.55,
      metalness: 0.0,
    })
    const alongZ = D >= W
    const legY = -H / 2 - b - legH / 2
    for (const s of [-1, 1]) {
      const leg = alongZ
        ? new THREE.Mesh(new THREE.BoxGeometry(legS, legH, bd), legMat)
        : new THREE.Mesh(new THREE.BoxGeometry(bw, legH, legS), legMat)
      if (alongZ) {
        leg.position.set(s * (bw / 2 - legS / 2), legY, 0)
      } else {
        leg.position.set(0, legY, s * (bd / 2 - legS / 2))
      }
      group.add(leg)
    }

    // Black agrotextile lining the interior: an inward-facing box renders
    // the inner walls and the bottom seen through the open top
    const fabricMat = new THREE.MeshStandardMaterial({
      map: getFabricTexture(),
      bumpMap: getFabricTexture(),
      bumpScale: 0.004,
      roughness: 0.95,
      metalness: 0.0,
      side: THREE.BackSide,
    })
    const eps = 0.0005
    const fabricMesh = new THREE.Mesh(
      new THREE.BoxGeometry(
        Math.max(W - 2 * t - eps, eps),
        Math.max(H - eps, eps),
        Math.max(D - 2 * t - eps, eps),
      ),
      fabricMat,
    )
    group.add(fabricMesh)

    // Re-center vertically so the extra bottom + legs don't skew the view
    group.position.y = (b + legH) / 2

    _boxMesh = group
    _scene.add(group)

    const w = canvas.offsetWidth || 495
    const h = Math.round(w * (355 / 495))

    const lineMat = new LineMaterial({
      color: baseColor.clone().multiplyScalar(0.3).getHex(),
      linewidth: 2,
    })
    lineMat.resolution.set(w, h)

    _lineMesh = new LineSegments2(
      buildLineGeo(W, H, D, seg, t, { b, bw, bd, legS, legH, alongZ }),
      lineMat,
    )
    _lineMesh.position.y = group.position.y
    _scene.add(_lineMesh)

    // Update renderer size in case container resized
    _renderer.setSize(w, h)
    _camera.aspect = w / h
    _camera.updateProjectionMatrix()

    // Keep the whole box in frame as it grows beyond its initial size, by
    // scaling the camera distance proportionally to the initial framing -
    // never recomputed from the raw fit-formula again, so a no-op redraw
    // (e.g. just clicking a slider without moving it) can't "jump" the
    // camera due to rounding differences between the two formulas. Only
    // ever pulls the camera back (never in), so shrinking the box doesn't
    // distort scale perception by zooming in on it. Preserves whatever
    // angle the user rotated to - only the distance is rescaled.
    const maxDim = Math.max(W, H, D)

    if (!_cameraReady) {
      const idealDist = (maxDim / (2 * Math.tan((28 * Math.PI) / 180 / 2))) * 1.3
      _camera.position.set(idealDist * 0.65, idealDist * 0.45, idealDist * 0.9)
      _camera.lookAt(0, 0, 0)
      _cameraReady = true
      _baseMaxDim = maxDim
      _baseDist = _camera.position.length()
    } else if (maxDim > _baseMaxDim) {
      const scaledDist = _baseDist * (maxDim / _baseMaxDim)
      if (scaledDist > _camera.position.length()) {
        const dir = _camera.position.clone().normalize()
        _camera.position.copy(dir.multiplyScalar(scaledDist))
      }
    }
    _lastMaxDim = maxDim
    _controls.update()

    _renderer.render(_scene, _camera)
  }

  // Restores the camera to its initial framing/angle for the current box
  // size and re-anchors the grow-only scaling baseline to it, so the next
  // recalculation doesn't immediately pull the distance back out again.
  const resetView = () => {
    if (!_camera || !_controls || !_lastMaxDim) return
    const idealDist = (_lastMaxDim / (2 * Math.tan((28 * Math.PI) / 180 / 2))) * 1.3
    _camera.position.set(idealDist * 0.65, idealDist * 0.45, idealDist * 0.9)
    _controls.target.set(0, 0, 0)
    _camera.lookAt(0, 0, 0)
    _baseMaxDim = _lastMaxDim
    _baseDist = _camera.position.length()
    _controls.update()
    _renderer.render(_scene, _camera)
  }

  // OrbitControls.autoRotate only advances while update() is called every
  // frame, so it needs its own render-loop for as long as it's enabled -
  // the normal 'change' listener only fires on user interaction.
  const autoRotateLoop = () => {
    if (!_controls || !_controls.autoRotate) {
      _autoRotateRAF = null
      return
    }
    _controls.update()
    _renderer.render(_scene, _camera)
    _autoRotateRAF = requestAnimationFrame(autoRotateLoop)
  }

  const setAutoRotate = (on) => {
    if (!_controls) return
    _controls.autoRotate = on
    if (on && !_autoRotateRAF) {
      _autoRotateRAF = requestAnimationFrame(autoRotateLoop)
    }
  }

  const formatPrice = (x) =>
    Number(x)
      .toFixed(2)
      .replace('.', ',')
      .replace(/\B(?=(\d{3})+(?!\d))/g, ' ') + ' zł'

  // Keep the WC add-to-cart form's hidden fields in sync on every
  // recalculation (not just at submit time) - WooCommerce's own AJAX
  // add-to-cart submit handler is bound to the same form and can read its
  // data before a submit-time-only sync would run, sending stale values.
  const syncHiddenFields = (data) => {
    if (!data) return
    const wcForm = document.querySelector('form.cart')
    if (!wcForm) return

    const values = {
      disp_length_hidden: data.length,
      disp_width_hidden: data.width,
      disp_height_hidden: data.height,
      disp_segments_hidden: data.segments,
      disp_insulation_hidden: data.insulation,
      disp_kant_hidden: data.kant,
      disp_color_hidden: data.colorLabel,
      disp_calculated_price: data.price.toFixed(2),
    }

    Object.entries(values).forEach(([name, value]) => {
      let input = wcForm.querySelector(`[name="${name}"]`)
      if (!input) {
        input = document.createElement('input')
        input.type = 'hidden'
        input.name = name
        wcForm.appendChild(input)
      }
      input.value = String(value)
    })
  }

  const getKant = (container) => {
    const checked = container.querySelector('input[name="disp_kant"]:checked')
    if (!checked) return { thickness: 4.5, segmentHeight: 4.5 }

    const val = checked.value.replace(',', '.').replace(/\s/g, '')

    if (val.includes('x')) {
      const [thickness, height] = val.split('x').map(Number)
      return { thickness, segmentHeight: height }
    }

    const size = Number(val)
    return { thickness: size, segmentHeight: size }
  }

  // Swap the 3D canvas for a plain message while dimensions are invalid
  const setInvalidState = (canvas, invalid) => {
    const wrap = canvas ? canvas.parentElement : null
    if (!wrap) return
    let msg = wrap.querySelector('.disp-invalid-msg')
    if (invalid) {
      if (!msg) {
        msg = document.createElement('div')
        msg.className = 'disp-invalid-msg'
        msg.textContent = 'Wprowadź poprawne wymiary'
        msg.style.cssText =
          'display:flex;align-items:center;justify-content:center;min-height:200px;color:#888;font-size:16px;'
        wrap.appendChild(msg)
      }
      msg.style.display = 'flex'
      canvas.style.display = 'none'
    } else {
      canvas.style.display = ''
      if (msg) msg.style.display = 'none'
    }
  }

  // Keep each slider's paired numeric input (and vice versa) in sync
  const syncSliders = (container) => {
    container.querySelectorAll('.disp-slider').forEach((slider) => {
      const target = container.querySelector(`[name="${slider.dataset.target}"]`)
      if (!target) return
      const v = parseFloat(target.value)
      if (!isNaN(v)) slider.value = String(clamp(v, Number(slider.min), Number(slider.max)))
    })
  }

  const colorMap = {
    brak: 'Brak - impregnacja',
    teak: 'Teak',
    orzech: 'Orzech',
    szary: 'Szary',
  }

  // Max number of stacked segments per kantówka type, per client request.
  const KANT_MAX_SEGMENTS = {
    '4.5 x 4.5': 20,
    '4.5 x 7': 15,
  }

  const bagsWord = (n) => {
    if (n === 1) return 'worek'
    const lastDigit = n % 10
    const lastTwo = n % 100
    if (lastDigit >= 2 && lastDigit <= 4 && !(lastTwo >= 12 && lastTwo <= 14)) return 'worki'
    return 'worków'
  }

  const calculate = (container) => {
    const canvas = container.querySelector('#disp-canvas')

    const dlInput = container.querySelector('[name="disp_length"]')
    const szInput = container.querySelector('[name="disp_width"]')
    const segInput = container.querySelector('[name="disp_segments"]')
    const segSlider = container.querySelector('.disp-slider[data-target="disp_segments"]')
    const insulationInput = container.querySelector('[name="disp_insulation"]')

    if (!dlInput || !szInput || !segInput) return null

    const kantChecked = container.querySelector('input[name="disp_kant"]:checked')
    const kantVal = kantChecked ? kantChecked.value : '4.5 x 4.5'
    const maxSeg = KANT_MAX_SEGMENTS[kantVal] || 20
    segInput.max = String(maxSeg)
    if (segSlider) segSlider.max = String(maxSeg)

    // Switching to a kantówka type with a lower cap should pull an
    // out-of-range value back down instead of showing an invalid state.
    if (segInput.value.trim() !== '' && Number(segInput.value) > maxSeg) {
      segInput.value = String(maxSeg)
      if (segSlider) segSlider.value = String(maxSeg)
    }

    const dlRaw = parseFloat(dlInput.value)
    const szRaw = parseFloat(szInput.value)
    const segRaw = parseInt(segInput.value, 10)

    const isValid =
      dlInput.value.trim() !== '' && !isNaN(dlRaw) && dlRaw >= 1 && dlRaw <= 200 &&
      szInput.value.trim() !== '' && !isNaN(szRaw) && szRaw >= 1 && szRaw <= 200 &&
      segInput.value.trim() !== '' && !isNaN(segRaw) && segRaw >= 1 && segRaw <= maxSeg

    setInvalidState(canvas, !isValid)

    if (!isValid) {
      const heightInfo = container.querySelector('#disp-height-result')
      if (heightInfo) heightInfo.textContent = '-'
      const soilBox = container.querySelector('#disp-soil-result')
      if (soilBox) soilBox.textContent = '-'
      const bagsBox = container.querySelector('#disp-bags-result')
      if (bagsBox) bagsBox.textContent = '-'
      const priceBox = document.querySelector('#disp-final-price')
      if (priceBox) priceBox.textContent = '-'
      return null
    }

    const dl = clamp(dlRaw, 1, 200)
    const sz = clamp(szRaw, 1, 200)
    const seg = clamp(segRaw, 1, maxSeg)

    const kant = getKant(container)
    const realHeight = seg * kant.segmentHeight
    const crossSection = kant.thickness * kant.segmentHeight

    const oci = insulationInput && insulationInput.checked ? 1.2 : 1

    const colorInput = container.querySelector(
      'input[name="disp_color"]:checked',
    )
    const color = colorInput ? colorInput.value : 'brak'
    const colorMultiplier = color !== 'brak' ? 1.1 : 1

    const m3Price = Number(window.dispCalc?.m3Price ?? 0)
    const volumeM3 = ((dl + sz) * 2 * crossSection * seg) / 1000000
    const basePrice = volumeM3 * oci * m3Price
    const finalPrice = basePrice * colorMultiplier

    const heightInfo = container.querySelector('#disp-height-result')
    if (heightInfo) heightInfo.textContent = String(realHeight)

    let liters = 0
    const soilBox = container.querySelector('#disp-soil-result')
    if (soilBox) {
      const innerL = Math.max(dl - 9, 0)
      const innerW = Math.max(sz - 9, 0)
      liters = ((innerL * innerW * realHeight) / 1000) * 0.9
      soilBox.textContent = liters.toFixed(1)
    }
    const bags = Math.ceil(liters / 50)
    const bagsBox = container.querySelector('#disp-bags-result')
    if (bagsBox) bagsBox.textContent = String(bags)
    const bagsWordEl = container.querySelector('#disp-bags-word')
    if (bagsWordEl) bagsWordEl.textContent = bagsWord(bags)

    const priceBox = document.querySelector('#disp-final-price')
    if (priceBox) priceBox.textContent = formatPrice(finalPrice)

    // Build the cart-facing result and sync it to the WC form's hidden
    // fields BEFORE touching the (WebGL-dependent, occasionally throwing)
    // 3D redraw below - the price shown and the price actually added to
    // the cart must never depend on whether the preview render succeeds.
    const kantLabel = kantChecked
      ? kantChecked.value
          .replace(/\s/g, '')
          .replace(/\./g, ',')
          .replace('x', ' x ') + ' cm'
      : ''

    const insulationLabel =
      insulationInput && insulationInput.checked ? 'tak' : 'nie'

    const colorLabel = colorMap[color] || 'Brak - impregnacja'

    const result = {
      length: dl,
      width: sz,
      height: realHeight,
      segments: seg,
      insulation: insulationLabel,
      kant: kantLabel,
      colorLabel,
      price: finalPrice,
    }
    syncHiddenFields(result)

    if (canvas)
      drawBox(canvas, sz, realHeight, dl, seg, color, kant.thickness)

    return result
  }

  // Occasionally the very first WebGL render throws (observed as a Three.js
  // "Cannot read properties of undefined (reading 'replace')" error deep in
  // shader program compilation, more often on slow-loading pages) and the
  // canvas is then stuck showing nothing until the page is manually
  // refreshed. Recover automatically instead: tear down and rebuild the
  // whole scene from scratch and retry a few times before giving up.
  let _calcRetryCount = 0
  const MAX_CALC_RETRIES = 5
  let _pendingAutoRotate = true

  const safeCalculate = (container) => {
    try {
      const result = calculate(container)
      _calcRetryCount = 0
      if (_pendingAutoRotate) {
        setAutoRotate(true)
        _pendingAutoRotate = false
      }
      const loader = document.querySelector('#disp-preview-loader')
      if (loader && !loader.classList.contains('is-hidden')) {
        requestAnimationFrame(() => loader.classList.add('is-hidden'))
      }
      return result
    } catch (err) {
      console.error('[disp-calculator-3d] render failed', err)
      resetThreeState()
      _pendingAutoRotate = true

      const loader = document.querySelector('#disp-preview-loader')
      if (loader) {
        loader.classList.remove('is-hidden')
        const p = loader.querySelector('p')
        if (p) {
          p.textContent =
            _calcRetryCount < MAX_CALC_RETRIES
              ? 'Wczytywanie podglądu'
              : 'Nie udało się wczytać podglądu. Odśwież stronę.'
        }
      }

      if (_calcRetryCount < MAX_CALC_RETRIES) {
        _calcRetryCount++
        setTimeout(() => safeCalculate(container), 300)
      }
      return null
    }
  }

  document.addEventListener('DOMContentLoaded', () => {
    // Guard against the calculator initializing twice on the same page
    // (e.g. a script duplicated by a caching/optimization plugin), which
    // would otherwise race two WebGL contexts against each other.
    if (window.__dispCalc3DInit) return
    window.__dispCalc3DInit = true

    const container = document.getElementById('disp-calculator')
    if (!container) return

    const clampInput = (input, min, max, isInt) => {
      input.addEventListener('blur', () => {
        const v = isInt ? parseInt(input.value, 10) : parseFloat(input.value)
        if (!isNaN(v)) {
          input.value = String(clamp(v, min, max))
          syncSliders(container)
          safeCalculate(container)
        }
      })
    }

    const dlInput = container.querySelector('[name="disp_length"]')
    const szInput = container.querySelector('[name="disp_width"]')
    const segInput = container.querySelector('[name="disp_segments"]')
    if (dlInput) clampInput(dlInput, 1, 200, false)
    if (szInput) clampInput(szInput, 1, 200, false)
    if (segInput) {
      // The max depends on the currently selected kantówka type - calculate()
      // keeps segInput.max in sync on every run, so read it live at blur time
      // rather than capturing a static value here.
      segInput.addEventListener('blur', () => {
        const v = parseInt(segInput.value, 10)
        if (!isNaN(v)) {
          segInput.value = String(clamp(v, 1, Number(segInput.max) || 20))
          syncSliders(container)
          safeCalculate(container)
        }
      })
    }

    container.querySelectorAll('.disp-slider').forEach((slider) => {
      const target = container.querySelector(`[name="${slider.dataset.target}"]`)
      if (!target) return
      // Only sync the paired numeric input here - the resulting 'input'
      // event bubbles up and triggers the (throttled) recalculation below,
      // so calling calculate() here too would rebuild the 3D scene twice
      // per event.
      slider.addEventListener('input', () => {
        target.value = slider.value
      })
    })

    // Coalesce bursts of events (e.g. dragging a slider fires many per
    // second) into a single recalculation/redraw per animation frame.
    let calcScheduled = false
    const scheduleCalculate = () => {
      if (calcScheduled) return
      calcScheduled = true
      requestAnimationFrame(() => {
        calcScheduled = false
        safeCalculate(container)
      })
    }

    container.addEventListener('input', scheduleCalculate)
    container.addEventListener('change', scheduleCalculate)

    safeCalculate(container)

    const autoRotateBtn = document.querySelector('#disp-autorotate-toggle')
    if (autoRotateBtn) {
      autoRotateBtn.addEventListener('click', () => {
        const next = autoRotateBtn.getAttribute('aria-pressed') !== 'true'
        autoRotateBtn.setAttribute('aria-pressed', String(next))
        autoRotateBtn.classList.toggle('is-active', next)
        setAutoRotate(next)
      })
    }

    const resetViewBtn = document.querySelector('#disp-reset-view')
    if (resetViewBtn) {
      resetViewBtn.addEventListener('click', () => {
        resetView()
        setAutoRotate(true)
        if (autoRotateBtn) {
          autoRotateBtn.setAttribute('aria-pressed', 'true')
          autoRotateBtn.classList.add('is-active')
        }
        resetViewBtn.classList.add('is-hidden')
      })
    }

    const resetBtn = document.querySelector('#disp-reset-config')
    if (resetBtn) {
      resetBtn.addEventListener('click', (e) => {
        e.preventDefault()
        container.querySelectorAll('input').forEach((input) => {
          if (input.type === 'radio' || input.type === 'checkbox') {
            input.checked = input.defaultChecked
          } else {
            input.value = input.defaultValue
          }
        })
        syncSliders(container)
        safeCalculate(container)
        window.scrollTo({ top: 0, behavior: 'smooth' })
      })
    }

  })
})()
